import React from "react";

export default function SummaryTab({ metrics }) {
  if (!metrics) {
    return (
      <div className="flex justify-center items-center flex-col space-y-3 py-16">
        <img src="/images/coming-soon.svg" alt="No data" className="w-32 h-32" />
        <p className="text-lg text-gray-500">Summary data unavailable</p>
      </div>
    );
  }

  const getTone = (label) => {
    if (!label) return "secondary";
    const lowerLabel = label.toLowerCase();
    if (
      lowerLabel.includes("excellent") ||
      lowerLabel.includes("good") ||
      lowerLabel.includes("well above recommended")
    )
      return "success";
    if (
      lowerLabel.includes("moderate") ||
      lowerLabel.includes("average") ||
      lowerLabel.includes("needs improvement")
    )
      return "warning";
    if (lowerLabel.includes("poor")) return "destructive";
    return "secondary";
  };

  const getBadgeDotColor = (tone) => {
    switch (tone) {
      case "success":
        return "bg-green-500";
      case "warning":
        return "bg-[#F79009]";
      case "destructive":
      default:
        return "bg-gray-500";
    }
  };

  const summaryItems = [
    {
      label: "Talk to listen ratio",
      value: metrics.talk_listen_ratio || 0,
      badge: metrics.talk_listen_ratio_label || "Unknown",
      tone: getTone(metrics.talk_listen_ratio_label),
    },
    {
      label: "Filler words",
      value: `${metrics.filler_words || 0} Words`,
      badge: metrics.filler_words_label || "Unknown",
      tone: getTone(metrics.filler_words_label),
    },
    {
      label: "Talk speed",
      value: `${metrics.talk_speed_wpm || 0} WPM`,
      badge: metrics.talk_speed_label || "Unknown",
      tone: getTone(metrics.talk_speed_label),
    },
    {
      label: "Longest monologue",
      value: metrics.longest_monologue?.label || "0 sec",
      badge: metrics.longest_monologue?.rating || "Unknown",
      tone: getTone(metrics.longest_monologue?.rating),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Summary Metrics Grid */}
      <div className="grid grid-cols-2 pb-6 border-b border-[#E9EAEB] dark:border-[#22262F]">
        {summaryItems.map((item, index) => (
          <div
            key={index}
            // style={{
            //   borderRight: index === 0 ? "1px solid #E9EAEB" : "none",
            //   borderBottom: index === 0 ? "1px solid #E9EAEB" : "none",
            // }}
            className={`space-y-2 ${
              index === 0
                ? "pr-4 pb-4 border-r border-b border-[#E9EAEB] dark:border-[#22262F]  "
                : index === 1
                  ? "pl-4 pb-4 border-b border-[#E9EAEB] dark:border-[#22262F]"
                  : index === 2
                    ? "pr-4 pt-4 border-r border-[#E9EAEB] dark:border-[#22262F]"
                    : "pl-4 pt-4"
            }`}
          >
            <div className="text-sm text-[#535862] dark:text-[#94969C] font-semibold">{item.label}</div>
            <div className="text-2xl font-semibold text-[#181D27] dark:text-[#F7F7F7]">{item.value}</div>
            <div className="inline-flex items-center gap-2 border border-[#D5D7DA] dark:border-[#373A41] bg-white dark:bg-[#0C0E12] rounded-[6px] px-[10px] py-[2px] h-[24px] shadow-sm">
              <div className={`w-2 h-2 rounded-full ${getBadgeDotColor(item.tone)}`}></div>
              <span className="text-sm font-medium text-gray-700 dark:text-[#CECFD2]">{item.badge}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Call Takeaways */}
      {metrics.call_takeaways && metrics.call_takeaways.length > 0 && (
        <div className="pb-6 border-b border-[#E9EAEB] dark:border-[#22262F]">
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
            <div className="w-2.5 h-2.5 bg-[#9E77ED] rounded-full flex-shrink-0"></div>
            <span className="text-sm font-semibold text-[#414651] dark:text-[#CECFD2]">Call Takeaways</span>
          </h4>
          <ul className="space-y-2">
            {metrics.call_takeaways.map((takeaway, index) => (
              <li key={index} className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-gray-500 rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-sm text-[#475467] dark:text-[#94969C]">{takeaway}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Needs Improvement */}
      {metrics.needs_improvement && metrics.needs_improvement.length > 0 && (
        <div className="pb-6 border-b border-[#E9EAEB] dark:border-[#22262F]">
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
            <div className="w-2.5 h-2.5 bg-[#D92D20] rounded-full flex-shrink-0"></div>
            <span className="text-sm font-semibold text-[#414651] dark:text-[#CECFD2]">Need to improve</span>
          </h4>
          <ul className="space-y-2">
            {metrics.needs_improvement.map((item, index) => (
              <li key={index} className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-gray-500 rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-sm text-[#475467] dark:text-[#94969C]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Strengths */}
      {metrics.strengths && metrics.strengths.length > 0 && (
        <div className="pb-6 ">
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
            <div className="w-2.5 h-2.5 bg-[#47CD89] rounded-full flex-shrink-0"></div>
            <span className="text-sm font-semibold text-[#414651] dark:text-[#CECFD2]">Strengths</span>
          </h4>
          <ul className="space-y-2">
            {metrics.strengths.map((strength, index) => (
              <li key={index} className="flex items-start space-x-2">
                <div className="w-1.5 h-1.5 bg-gray-500 rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-sm text-[#475467] dark:text-[#94969C]">{strength}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
